import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import MediaAPI from "../../../../api/MediaAPI";
import { getSelectClassNames } from "../../../../components/form/Select";
import Spinner from "../../../../components/utils/Spinner";

import type { JSX } from "react";

import type {
  MediaWithTrailers,
  MediaWithTrailersItem,
} from "../../../../api/MediaAPI.types";
import type { HighlightParamsHosted } from "../../../../api/ProjectAPI.types";

interface HighlightHostedConfigProps {
  componentParams: HighlightParamsHosted;
  onChange: (
    params: Pick<HighlightParamsHosted, "mediaId" | "trailerId">,
  ) => void;
}

interface MediaOption {
  value: string;
  label: string;
  trailers: MediaWithTrailers[];
}

interface TrailerOption {
  value: string;
  label: string;
}

function HighlightHostedConfig({
  componentParams,
  onChange,
}: HighlightHostedConfigProps): JSX.Element {
  const { t } = useTranslation("editor-highlight");
  const getMediasWithTrailersListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediasWithTrailersList,
    params: [],
  });
  const {
    isLoading: isMediasWithTrailerListLoading,
    data: mediasWithTrailerList,
  } = useQuery({
    ...getMediasWithTrailersListQuery,
  });

  if (isMediasWithTrailerListLoading) {
    return (
      <div className="m-[1.875rem]">
        <Spinner />
      </div>
    );
  }

  const mediaOptionList: MediaOption[] = (mediasWithTrailerList ?? []).map(
    (media: MediaWithTrailersItem) => ({
      value: media.uuid,
      label: media.label,
      trailers: media.trailers,
    }),
  );

  const selectedMedia =
    mediaOptionList.find(
      (option) => option.value === componentParams?.mediaId,
    ) ?? null;

  const trailerOptionList: TrailerOption[] = (
    selectedMedia?.trailers ?? []
  ).map((trailer) => ({
    value: trailer.uuid,
    label: trailer.label,
  }));

  const selectedTrailer =
    trailerOptionList.find(
      (option) => option.value === componentParams?.trailerId,
    ) ?? null;

  return (
    <div className="my-[0.625rem] flex flex-col">
      <div className="py-[0.625rem] text-[0.9375rem] font-medium">
        {t("mediaListAvalaible")}
      </div>
      <div className="w-[350px] max-md:w-full">
        <Select<MediaOption>
          unstyled
          classNames={getSelectClassNames()}
          inputId="select-media-highlight"
          isLoading={isMediasWithTrailerListLoading}
          options={mediaOptionList}
          placeholder={t("selectMedia")}
          styles={{ control: () => ({}) }}
          value={selectedMedia}
          onChange={(selected) => {
            onChange({
              mediaId: selected?.value ?? null,
              trailerId: selected?.trailers?.[0]?.uuid ?? null,
            });
          }}
        />
      </div>
      <div className="py-[0.625rem] text-[0.9375rem] font-medium">
        {t("trailerListAvalaible")}
      </div>
      <div className="w-[350px] max-md:w-full">
        <Select<TrailerOption>
          key={`trailer-${componentParams?.mediaId}`}
          unstyled
          classNames={getSelectClassNames()}
          inputId="select-media-trailer-highlight"
          isLoading={isMediasWithTrailerListLoading}
          options={trailerOptionList}
          placeholder={t("selectTrailer")}
          styles={{ control: () => ({}) }}
          value={selectedTrailer}
          onChange={(selected) => {
            if (!componentParams?.mediaId) return;
            onChange({
              ...componentParams,
              trailerId: selected?.value ?? null,
            });
          }}
        />
      </div>
    </div>
  );
}

export default HighlightHostedConfig;
